import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface RedactionRuleRow {
  readonly genaiRedactionRuleId: string;
  readonly ruleCode: string;
  readonly ruleName: string;
  readonly redactionType: string;
  readonly regexPattern: string;
  readonly replacementToken: string;
  readonly severity: string;
  readonly priority: number;
  readonly isActive: boolean;
}

const REDACTION_TYPES = ['CUSTOM', 'SSN', 'CREDIT_CARD', 'EMAIL', 'PHONE', 'IP_ADDRESS'];
const SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

/**
 * M38 — GenAI Redaction-Rule Console (GR_M36-M40 §L38.09 / §M row 14 / §Q9). The
 * admin surface over the DB-driven PII / secret redaction rule master (T513B):
 * data-protection officers author the regex patterns the guardrail engine applies
 * to a prompt BEFORE it ever reaches a model, so the redaction set is
 * configurable (LAW 6) rather than hard-coded. Each rule maps a redaction_type
 * (SSN / CREDIT_CARD / EMAIL / PHONE / CUSTOM …) to a regex_pattern +
 * replacement_token with a severity + priority. Viewing needs REDACTION_VIEW;
 * authoring / deleting needs REDACTION_MANAGE (SoD). Tenant isolation is enforced
 * server-side (LAW 4). This screen backs the /admin/genai/redaction-rules route.
 */
export function RedactionRulesConsole(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<RedactionRuleRow[]>([]);
  const [redactionTypeFilter, setRedactionTypeFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [ruleCode, setRuleCode] = useState('');
  const [ruleName, setRuleName] = useState('');
  const [redactionType, setRedactionType] = useState('CUSTOM');
  const [regexPattern, setRegexPattern] = useState('');
  const [regexFlags, setRegexFlags] = useState('gi');
  const [replacementToken, setReplacementToken] = useState('[REDACTED]');
  const [severity, setSeverity] = useState('MEDIUM');
  const [priority, setPriority] = useState('100');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (redactionTypeFilter) params.set('redactionType', redactionTypeFilter);
    apiRequest<{ data: RedactionRuleRow[] }>(`/v1/ai/redaction-rules?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, redactionTypeFilter, reload]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    setCreated(null);
    if (!ruleCode.trim() || !ruleName.trim() || !regexPattern.trim()) {
      setFormError(t('m38.redaction.required'));
      return;
    }
    try {
      // Fail fast client-side: never POST a regex the browser itself rejects.
      // eslint-disable-next-line no-new
      new RegExp(regexPattern, regexFlags || 'gi');
    } catch (reErr) {
      setFormError(t('m38.redaction.invalidRegex', { message: (reErr as Error).message }));
      return;
    }
    setBusy(true);
    try {
      await apiRequest<{ data: RedactionRuleRow }>('/v1/ai/redaction-rules', {
        method: 'POST',
        body: {
          ruleCode: ruleCode.trim(),
          ruleName: ruleName.trim(),
          redactionType,
          regexPattern: regexPattern.trim(),
          regexFlags: regexFlags.trim() || undefined,
          replacementToken: replacementToken.trim() || undefined,
          severity,
          priority: Number(priority) || 100,
          description: description.trim() || undefined,
        },
        token,
      });
      setCreated(t('m38.redaction.created', { code: ruleCode.trim() }));
      setRuleCode(''); setRuleName(''); setRegexPattern(''); setDescription('');
      setRegexFlags('gi'); setReplacementToken('[REDACTED]'); setPriority('100');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setFormError(t('m38.redaction.notAuthorised'));
      } else {
        setFormError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (r: RedactionRuleRow): Promise<void> => {
    try {
      await apiRequest(`/v1/ai/redaction-rules/${r.genaiRedactionRuleId}`, {
        method: 'PATCH',
        body: { isActive: !r.isActive },
        token,
      });
      setReload((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const remove = async (r: RedactionRuleRow): Promise<void> => {
    try {
      await apiRequest(`/v1/ai/redaction-rules/${r.genaiRedactionRuleId}`, { method: 'DELETE', token });
      setReload((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <main aria-label="redaction-rules-console">
      <h1>{t('m38.redaction.title')}</h1>
      <p>{t('m38.redaction.intro')}</p>

      <section aria-label="redaction-create">
        <h2>{t('m38.redaction.newHeading')}</h2>
        <form onSubmit={submit} aria-label="redaction-form">
          <label>{t('m38.redaction.ruleCode')}
            <input aria-label="rule-code" value={ruleCode} onChange={(e) => setRuleCode(e.target.value)} />
          </label>
          <label>{t('m38.redaction.ruleName')}
            <input aria-label="rule-name" value={ruleName} onChange={(e) => setRuleName(e.target.value)} />
          </label>
          <label>{t('m38.redaction.redactionType')}
            <select aria-label="redaction-type" value={redactionType} onChange={(e) => setRedactionType(e.target.value)}>
              {REDACTION_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m38.redaction.regexPattern')}
            <input aria-label="regex-pattern" value={regexPattern} onChange={(e) => setRegexPattern(e.target.value)} />
          </label>
          <label>{t('m38.redaction.regexFlags')}
            <input aria-label="regex-flags" value={regexFlags} onChange={(e) => setRegexFlags(e.target.value)} />
          </label>
          <label>{t('m38.redaction.replacementToken')}
            <input aria-label="replacement-token" value={replacementToken} onChange={(e) => setReplacementToken(e.target.value)} />
          </label>
          <label>{t('m38.common.severity')}
            <select aria-label="severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
              {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>{t('m38.redaction.priority')}
            <input aria-label="priority" type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
          </label>
          <label>{t('m38.common.description')}
            <input aria-label="description" value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <Button variant="primary" type="submit" disabled={busy}>{busy ? t('m38.common.saving') : t('m38.redaction.createBtn')}</Button>
        </form>
        {formError && <p role="alert">{formError}</p>}
        {created && <p aria-label="create-success">{created}</p>}
      </section>

      <section aria-label="redaction-filters">
        <label>{t('m38.redaction.filterByType')}
          <select aria-label="filter-type" value={redactionTypeFilter} onChange={(e) => setRedactionTypeFilter(e.target.value)}>
            <option value="">{t('m38.common.all')}</option>
            {REDACTION_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading
        ? <p>{t('common.loading')}</p>
        : (
          <DataTable<RedactionRuleRow>
            ariaLabel="redaction-table"
            testId="redaction-table"
            rowKey={(r) => r.genaiRedactionRuleId}
            rowAriaLabel={(r) => `rule-${r.ruleCode}`}
            emptyMessage={t('m38.redaction.noneDefined')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m38.common.code'), render: (r) => r.ruleCode },
              { key: 'name', header: t('m38.common.name'), render: (r) => r.ruleName },
              { key: 'type', header: t('m38.common.type'), render: (r) => r.redactionType },
              { key: 'pattern', header: t('m38.redaction.colPattern'), render: (r) => <code>{r.regexPattern}</code> },
              { key: 'replacement', header: t('m38.redaction.colReplacement'), render: (r) => r.replacementToken },
              { key: 'severity', header: t('m38.common.severity'), render: (r) => r.severity },
              { key: 'priority', header: t('m38.redaction.priority'), render: (r) => r.priority },
              { key: 'active', header: t('m38.common.active'), render: (r) => r.isActive ? t('common.yes') : t('common.no') },
              { key: 'actions', header: t('m38.common.actions'), render: (r) => (
                <>
                  <Button variant="secondary" type="button" aria-label={`toggle-${r.ruleCode}`} onClick={() => toggleActive(r)}>
                    {r.isActive ? t('m38.common.disable') : t('m38.common.enable')}
                  </Button>
                  <Button variant="danger" type="button" aria-label={`delete-${r.ruleCode}`} onClick={() => remove(r)}>{t('common.delete')}</Button>
                </>
              ) },
            ]}
          />
        )}
    </main>
  );
}
